import { useTranslation } from "react-i18next";
import style from "./infoSlabMobile.module.css";
import { createStyles, Progress, Box, Text, Group, SimpleGrid, rem, Badge, Flex } from "@mantine/core";
import { calculateAndCategorizeAIScore } from "../../hooks/calculateAndCategorizeAIScore";

const useStyles = createStyles((theme) => ({
    progressLabel: {
        fontFamily: `Inter, ${theme.fontFamily}`,
        lineHeight: 1,
        fontSize: "16px",
        fontWeight: "500",
    },

    stat: {
        borderBottom: `${rem(3)} solid`,
        paddingBottom: rem(5),
    },

    statCount: {
        fontFamily: `Inter, ${theme.fontFamily}`,
        lineHeight: 1.3,
    },

    diff: {
        fontFamily: `Inter, ${theme.fontFamily}`,
        display: "flex",
        alignItems: "center",
    },

    icon: {
        color: theme.colorScheme === "dark" ? theme.colors.dark[3] : theme.colors.gray[4],
    },
}));

const InfoSlabMobile = ({ scan, aiScore, alwaysVisible = false }) => {
    const { t } = useTranslation(["common"]);

    const { classes } = useStyles();

    const { aiScore: rawCalculatedAIScore, category: calculatedAiScoreCategoryName } = calculateAndCategorizeAIScore(aiScore, scan?.version, scan?.aiDetectionStrictMode);
    const calculatedAIScore = Math.min(99.5, Math.max(0.5, rawCalculatedAIScore));

    const segments = [
        {
            value: 100 - calculatedAIScore,
            color: "#0BB779",
            label: 100 - calculatedAIScore > 20 ? `${Math.ceil((100 - calculatedAIScore) * 100) / 100}%` : undefined,
        },
        {
            value: calculatedAIScore,
            color: "#E22024",
            label: calculatedAIScore > 20 ? `${calculatedAIScore}%` : undefined,
        },
    ];

    const titleMap = {
        AiGenerated: t("scan.result.title.ai"),
        ProbablyAiGenerated: t("scan.result.title.ai"),
        Unsure: t("scan.result.title.unsure"),
        ProbablyOriginal: t("scan.result.title.probably-original"),
        Original: t("scan.result.title.original"),
    };

    if (alwaysVisible || (calculatedAiScoreCategoryName === "AiGenerated" || calculatedAiScoreCategoryName === "ProbablyAiGenerated"))
        return (
            <div className={style.wrapper}>
                <svg width="300" height="339" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0 320h300c0 2.761-2.365 5-5.282 5H5.2817C2.3647 325 0 322.761 0 320Z" fill="url(#a)" />
                    <path d="m149.472 339-18.754-15h37.508l-18.754 15Z" fill="url(#b)" />
                    <path d="M5 .5h290c2.485 0 4.5 2.01472 4.5 4.5v314.5H.5V5C.5 2.51472 2.51472.5 5 .5Z" stroke="url(#c)" />
                    <defs>
                        <linearGradient id="a" x1="-9e-8" y1="312" x2="300" y2="312" gradientUnits="userSpaceOnUse">
                            <stop stop-color="#ED213A" />
                            <stop offset="1" stop-color="#CA3928" />
                        </linearGradient>
                        <linearGradient id="b" x1="-9e-8" y1="312" x2="300" y2="312" gradientUnits="userSpaceOnUse">
                            <stop stop-color="#ED213A" />
                            <stop offset="1" stop-color="#CA3928" />
                        </linearGradient>
                        <linearGradient id="c" x1="5.6e-7" y1="160" x2="300" y2="160" gradientUnits="userSpaceOnUse">
                            <stop stop-color="#ED213A" />
                            <stop offset="1" stop-color="#CA3928" />
                        </linearGradient>
                    </defs>
                </svg>
                <div className={style.content}>
                    <Group position="apart">
                        <Flex align="center" spacing="xs" justify={"space-between"} w={"100%"} wrap={"wrap"}>
                            <Text fz={"md"} fw={700}>
                                {titleMap[calculatedAiScoreCategoryName]}
                            </Text>

                            {scan?.aiDetectionStrictMode && (
                                <Badge radius={"xs"} color="indigo" mb={"0.25rem"} mt={"0.5rem"}>
                                    {t("scan.result.description.strict-mode")}
                                </Badge>
                            )}
                        </Flex>
                    </Group>

                    <div>
                        <Text fz="sm">
                            {`${t("scan.result.description.intro")} ${calculatedAIScore}% ${t("scan.result.description.explaination")}`}
                        </Text>
                    </div>

                    <Progress sections={segments} size={34} classNames={{ label: classes.progressLabel }} mt={40} aria-hidden="true" />
                    <SimpleGrid cols={2} mt="xl">
                        <Box key={"Orginal"} sx={{ borderBottomColor: "#0BB779" }} className={classes.stat}>
                            <Group position="apart" align="flex-end" spacing={0}>
                                <Text fw={600}>{t("scan.result.human")}</Text>
                                <Text c={"var(--green-dark)"} fw={700} size="sm">
                                    {Math.ceil((100 - calculatedAIScore) * 10) / 10}%
                                </Text>
                            </Group>
                        </Box>

                        <Box key={"Kunstig intelligens"} sx={{ borderBottomColor: "#E22024" }} className={classes.stat}>
                            <Group position="apart" align="flex-end" spacing={0}>
                                <Text fw={600}>{t("scan.result.ai")}</Text>
                                <Text c={"var(--red)"} fw={700} size="sm">
                                    {Math.floor(calculatedAIScore * 10) / 10}%
                                </Text>
                            </Group>
                        </Box>
                    </SimpleGrid>
                </div>
            </div>
        );

    return null;
};

export default InfoSlabMobile;
